<template>
  <Transition
    enter-active-class="transition-transform duration-200 ease-out"
    leave-active-class="transition-transform duration-200 ease-in"
    enter-from-class="[transform:translateX(100%)]"
    leave-to-class="[transform:translateX(100%)]"
  >
    <aside
      v-if="isOpen"
      class="fixed top-0 right-0 bottom-0 z-[1500] flex w-full flex-col border-l border-gray-200 bg-white shadow-2xl sm:w-[var(--clerk-ai-width)]"
      :style="panelStyle"
      aria-label="ClerkAI"
      data-cy="clerk-ai-panel"
    >
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- a focusable separator is the ARIA window-splitter pattern -->
      <div
        v-if="!expanded"
        class="absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize hover:bg-gray-200 focus:bg-gray-200 focus:outline-none !hidden sm:!block"
        :class="{ 'bg-gray-200': resizing }"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize ClerkAI"
        :aria-valuenow="panelWidth"
        :aria-valuemin="CLERK_AI_MIN_WIDTH"
        :aria-valuemax="CLERK_AI_MAX_WIDTH"
        tabindex="0"
        data-cy="clerk-ai-resize"
        @pointerdown="startResize"
        @pointermove="resize"
        @pointerup="endResize"
        @pointercancel="endResize"
        @keydown.left.prevent="resizeBy(CLERK_AI_RESIZE_STEP)"
        @keydown.right.prevent="resizeBy(-CLERK_AI_RESIZE_STEP)"
      />

      <header
        class="flex items-center gap-2 border-b border-gray-200 px-4 py-3"
      >
        <LlIcon
          icon="mdi-message-question-outline"
          color="primary"
          size="small"
        />
        <h2 class="m-0 flex-1 text-base font-semibold">ClerkAI</h2>
        <LlSecondaryButton
          size="small"
          variant="text"
          prepend-icon="mdi-plus"
          :disabled="isEmpty"
          data-cy="clerk-ai-new-chat"
          @click="clerkAi.newChat()"
        >
          New
        </LlSecondaryButton>
        <div class="!hidden sm:!block">
          <LlSecondaryButton
            :icon="expanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand'"
            variant="text"
            size="small"
            :aria-label="expanded ? 'Restore ClerkAI size' : 'Expand ClerkAI'"
            :title="expanded ? 'Restore size' : 'Expand'"
            :aria-pressed="expanded"
            data-cy="clerk-ai-expand"
            @click="clerkAi.toggleExpanded()"
          />
        </div>
        <LlSecondaryButton
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close ClerkAI"
          data-cy="clerk-ai-close"
          @click="clerkAi.close()"
        />
      </header>

      <p class="m-0 bg-blue-50 px-4 py-2 text-xs text-blue-700">
        This is a pre-release feature enabled for evaluation in your account. It
        may not be available in your current plan in the future.
      </p>

      <div
        ref="chatRef"
        class="flex flex-col overflow-y-auto text-sm"
        :class="{ 'flex-1 p-4': !isEmpty }"
        aria-live="polite"
        data-cy="clerk-ai-messages"
      >
        <div
          class="flex w-full flex-col gap-4"
          :class="{ 'mx-auto max-w-3xl': expanded }"
        >
          <template v-for="message in messages" :key="message.id">
            <div
              v-if="message.role === 'user'"
              class="max-w-[85%] self-end whitespace-pre-wrap rounded-2xl bg-gray-100 px-3 py-2"
              data-cy="clerk-ai-user-message"
            >
              {{ message.text }}
            </div>

            <div v-else class="w-full" data-cy="clerk-ai-assistant-message">
              <div
                v-if="message.status === 'pending'"
                class="flex items-center gap-2 text-gray-500"
              >
                <v-progress-circular
                  indeterminate
                  size="16"
                  width="2"
                  color="primary"
                />
                Thinking…
              </div>

              <div v-else-if="message.status === 'error'" class="text-red-700">
                <p class="m-0 mb-2">ClerkAI couldn't finish this answer.</p>
                <LlSecondaryButton
                  size="small"
                  :disabled="sending"
                  data-cy="clerk-ai-retry"
                  @click="retry(message.id)"
                >
                  Try again
                </LlSecondaryButton>
              </div>

              <template v-else>
                <template
                  v-for="(block, index) in parseSummary(message.summary)"
                  :key="index"
                >
                  <component
                    :is="block.ordered ? 'ol' : 'ul'"
                    v-if="block.type === 'list'"
                    :start="block.ordered ? block.start : undefined"
                    class="my-2 ml-0 space-y-1 pl-6"
                    :class="block.ordered ? 'list-decimal' : 'list-disc'"
                  >
                    <li
                      v-for="(item, itemIndex) in block.items"
                      :key="itemIndex"
                    >
                      <component
                        :is="segment.bold ? 'strong' : 'span'"
                        v-for="(segment, segmentIndex) in item"
                        :key="segmentIndex"
                        >{{ segment.text }}</component
                      >
                    </li>
                  </component>
                  <p
                    v-else
                    :class="
                      block.type === 'heading'
                        ? 'mb-1 mt-3 font-semibold'
                        : 'my-1'
                    "
                  >
                    <component
                      :is="segment.bold ? 'strong' : 'span'"
                      v-for="(segment, segmentIndex) in block.segments"
                      :key="segmentIndex"
                      >{{ segment.text }}</component
                    >
                  </p>
                </template>

                <div
                  v-if="message.table"
                  class="mt-2 overflow-hidden rounded-lg border border-gray-200"
                  data-cy="clerk-ai-table"
                >
                  <v-table
                    density="compact"
                    fixed-header
                    :height="message.table.rows.length > 8 ? 320 : undefined"
                    class="text-xs"
                  >
                    <thead>
                      <tr>
                        <th
                          v-for="(column, columnIndex) in message.table.columns"
                          :key="columnIndex"
                          scope="col"
                          class="whitespace-nowrap"
                        >
                          {{ column }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, rowIndex) in message.table.rows.slice(
                          0,
                          MAX_ROWS,
                        )"
                        :key="rowIndex"
                      >
                        <td
                          v-for="(cell, cellIndex) in row"
                          :key="cellIndex"
                          :class="[
                            'whitespace-nowrap',
                            typeof cell === 'number'
                              ? 'text-right tabular-nums'
                              : '',
                          ]"
                        >
                          {{ formatCell(cell) }}
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                  <p
                    v-if="message.table.rows.length > MAX_ROWS"
                    class="m-0 border-t border-gray-200 px-2 py-1.5 text-xs text-gray-500"
                  >
                    Showing the first {{ MAX_ROWS }} of
                    {{ message.table.rows.length }} rows. The download has them
                    all.
                  </p>
                </div>

                <!-- Thumbs up/down join this row later. -->
                <div
                  class="mt-1 flex items-center gap-0.5 text-gray-500"
                  data-cy="clerk-ai-message-actions"
                >
                  <LlSecondaryButton
                    :icon="
                      copiedId === message.id ? 'mdi-check' : 'mdi-content-copy'
                    "
                    variant="text"
                    size="small"
                    :aria-label="
                      copiedId === message.id ? 'Copied' : 'Copy answer'
                    "
                    :title="copiedId === message.id ? 'Copied' : 'Copy answer'"
                    data-cy="clerk-ai-copy"
                    @click="copyAnswer(message)"
                  />
                  <LlSecondaryButton
                    v-if="message.table"
                    icon="mdi-download"
                    variant="text"
                    size="small"
                    aria-label="Download as CSV"
                    title="Download as CSV"
                    data-cy="clerk-ai-download"
                    @click="downloadCsv(message)"
                  />
                </div>
              </template>
            </div>
          </template>
        </div>
      </div>

      <!-- Docked, pb-20 keeps the box clear of HubSpot's chat launcher. -->
      <div
        :class="[
          isEmpty ? 'my-auto px-6 pb-16' : 'px-3 pt-2 pb-20',
          { 'mx-auto w-full max-w-3xl': expanded },
        ]"
        data-cy="clerk-ai-composer"
      >
        <p
          v-if="isEmpty"
          class="m-0 mb-4 text-center text-lg font-medium text-gray-700"
          data-cy="clerk-ai-greeting"
        >
          How can I help?
        </p>
        <div
          v-if="isEmpty"
          class="mb-4 grid grid-cols-2 gap-2"
          data-cy="clerk-ai-starter-prompts"
        >
          <LlSecondaryButton
            v-for="prompt in CLERK_AI_STARTER_PROMPTS"
            :key="prompt.question"
            variant="outlined"
            class="!h-auto !justify-start !rounded-2xl !border-gray-200 !px-3 !py-2 !text-left !tracking-normal"
            :disabled="sending"
            data-cy="clerk-ai-starter-prompt"
            @click="ask(prompt.question)"
          >
            <span
              class="flex flex-col items-start gap-0.5 whitespace-normal normal-case"
            >
              <span class="text-xs font-semibold text-primary">
                {{ prompt.topic }}
              </span>
              <span
                class="text-sm font-normal text-gray-700"
                data-cy="clerk-ai-starter-prompt-question"
              >
                {{ prompt.question }}
              </span>
            </span>
          </LlSecondaryButton>
        </div>
        <!-- The textarea overrides undo the legacy Bootstrap border and inset shadow. -->
        <v-form
          class="rounded-3xl border border-gray-300 bg-white px-4 pt-2 pb-2 shadow-sm transition-colors focus-within:border-gray-400 focus-within:shadow-md [&_textarea]:!py-1 [&_textarea]:!text-sm [&_textarea]:!leading-5 [&_textarea]:!border-0 [&_textarea]:!shadow-none"
          @submit.prevent="submit"
          @keydown.enter.exact.prevent="submit"
          @keydown.esc="clerkAi.close()"
        >
          <LlTextarea
            ref="inputRef"
            v-model="draft"
            variant="plain"
            :rows="1"
            :max-rows="6"
            auto-grow
            no-resize
            placeholder="Ask about hours, billing, collections…"
            aria-label="Ask ClerkAI"
            data-cy="clerk-ai-input"
          />
          <!-- Left side is reserved for the upload "+" and the mode selector. -->
          <div
            class="flex min-h-[28px] items-center justify-end gap-2"
            data-cy="clerk-ai-composer-toolbar"
          >
            <span class="text-xs text-gray-500">
              {{ sending ? 'ClerkAI is answering…' : 'Enter to send' }}
            </span>
          </div>
        </v-form>
        <p
          v-if="isEmpty"
          class="m-0 mt-2 flex items-start justify-center gap-1 text-[11px] text-gray-500"
          data-cy="clerk-ai-privacy-note"
        >
          <LlIcon
            icon="mdi-shield-lock-outline"
            size="x-small"
            class="mt-0.5"
          />
          AI can make mistakes; verify before acting. <br />
          We don’t train on your data.
        </p>
      </div>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useClerkAiStore } from '@/stores/insights/useClerkAiStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  clampPanelWidth,
  csvColumnMap,
  csvFilename,
  formatCell,
  parseSummary,
  toPlainText,
} from '@/utils/clerkAiHelpers';
import { useExportUtilities } from '@/composables/useExportUtilities';
import type { ClerkAiAssistantMessage } from '@/models/clerkAiTypes';
import {
  CLERK_AI_MAX_WIDTH,
  CLERK_AI_MIN_WIDTH,
  CLERK_AI_RESIZE_STEP,
  CLERK_AI_STARTER_PROMPTS,
} from '@/constants/clerkAiConstants';
import LlIcon from '@/components/LlIcon.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTextarea from '@/components/LlTextarea.vue';

const MAX_ROWS = 200;

const clerkAi = useClerkAiStore();
const { isOpen, messages, sending, isEmpty, width, expanded } =
  storeToRefs(clerkAi);
const { handleError } = useSnackbarHandler();

const draft = ref('');
const chatRef = ref<HTMLElement | null>(null);
const inputRef = ref<{ focus: () => void } | null>(null);

const viewportWidth = ref(window.innerWidth);
const resizing = ref(false);

const panelWidth = computed(() =>
  clampPanelWidth(width.value, viewportWidth.value),
);

// Width eases between sizes on expand/restore, but follows a drag directly.
const panelStyle = computed(() => ({
  '--clerk-ai-width': expanded.value ? '100%' : `${panelWidth.value}px`,
  transition: resizing.value
    ? 'transform 200ms ease-out'
    : 'transform 200ms ease-out, width 200ms ease-out',
}));

const onWindowResize = () => {
  viewportWidth.value = window.innerWidth;
};

onMounted(() => window.addEventListener('resize', onWindowResize));

const startResize = (event: PointerEvent) => {
  event.preventDefault();
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  resizing.value = true;
};

const resize = (event: PointerEvent) => {
  if (!resizing.value) return;
  clerkAi.setWidth(viewportWidth.value - event.clientX, viewportWidth.value);
};

const endResize = (event: PointerEvent) => {
  if (!resizing.value) return;
  resizing.value = false;
  clerkAi.persistWidth();
  // On pointercancel the browser may already have dropped the capture; releasing it again throws.
  const handle = event.currentTarget as HTMLElement;
  if (handle.hasPointerCapture(event.pointerId)) {
    handle.releasePointerCapture(event.pointerId);
  }
};

const resizeBy = (delta: number) => {
  clerkAi.setWidth(panelWidth.value + delta, viewportWidth.value);
  clerkAi.persistWidth();
};

onBeforeUnmount(() => {
  clearTimeout(copiedTimer);
  window.removeEventListener('resize', onWindowResize);
});

const canSend = computed(() => !sending.value && draft.value.trim().length > 0);

const scrollToBottom = () =>
  nextTick(() => {
    if (chatRef.value) chatRef.value.scrollTop = chatRef.value.scrollHeight;
  });

watch(isOpen, (open) => {
  if (!open) return;
  scrollToBottom().then(() => inputRef.value?.focus());
});

const messageProgress = computed(() =>
  messages.value
    .map((m) => (m.role === 'assistant' ? `${m.id}:${m.status}` : m.id))
    .join(','),
);

watch(messageProgress, scrollToBottom);

const ask = async (question: string) => {
  try {
    await clerkAi.send(question);
  } catch (error) {
    handleError(error);
  }
};

const submit = () => {
  if (!canSend.value) return;
  const question = draft.value;
  draft.value = '';
  ask(question);
};

const { exportCSV } = useExportUtilities();
const copiedId = ref<number | null>(null);
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

const copyAnswer = async (message: ClerkAiAssistantMessage) => {
  try {
    await navigator.clipboard.writeText(
      toPlainText(message.summary, message.table),
    );
    copiedId.value = message.id;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      copiedId.value = null;
    }, 2000);
  } catch {
    // The browser's own message ("Write permission denied") means nothing to a user.
    handleError(
      "Your browser didn't allow copying. Select the answer and copy it instead.",
    );
  }
};

const downloadCsv = async (message: ClerkAiAssistantMessage) => {
  if (!message.table) return;
  try {
    await exportCSV(message.table.rows, csvColumnMap(message.table), {
      filename: csvFilename(message.question),
    });
  } catch (error) {
    handleError(error);
  }
};

const retry = async (id: number) => {
  try {
    await clerkAi.retry(id);
  } catch (error) {
    handleError(error);
  }
};
</script>
